import { ActionIcon, Tooltip } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { Scanner, setZXingModuleOverrides } from '@yudiel/react-qr-scanner'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import wasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url'
import classes from './QrViewfinder.module.css'

// The QR decoder's WebAssembly ships with the app instead of being fetched from a public CDN
setZXingModuleOverrides({ locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasmUrl : prefix + path) })

export interface QrViewfinderProps {
  /** A QR was read: its text */
  onCode: (text: string) => void
  /** No camera, or no permission for it */
  onError: () => void
  /** Shapes it instead of the square: a scanning screen gives it a height to fill */
  className?: string
  /** What it reads: QR codes, and where the paper carries one, the bill's barcode too (`code_128`) */
  formats?: ('qr_code' | 'code_128')[]
  /** Turns the camera off: a × at its top right. Every scanner has one, so the camera never stays on with no way out */
  onClose?: () => void
}

/**
 * The back camera reading QR codes, square, with white corner marks like a phone's camera app
 * (the library's own finder is red, which reads as an error). Until the first frame plays —
 * the permission prompt, a slow camera — it says so instead of sitting black.
 */
export function QrViewfinder({ onCode, onError, className, formats = ['qr_code'], onClose }: QrViewfinderProps) {
  const { t } = useTranslation()
  const [live, setLive] = useState(false)
  return (
    <div className={className ? `${classes.viewfinder} ${className}` : classes.viewfinder} onPlayingCapture={() => setLive(true)}>
      <Scanner
        formats={formats}
        constraints={{ facingMode: 'environment' }}
        sound={false}
        onScan={(codes) => {
          const text = codes[0]?.rawValue
          if (text) onCode(text)
        }}
        onError={onError}
        components={{ finder: false, torch: true }}
        styles={{ container: { width: '100%', height: '100%' }, video: { objectFit: 'cover' } }}
      />
      <span className={classes.corners} aria-hidden="true" />
      {!live && <span className={classes.starting}>{t('scan.starting')}</span>}
      {onClose && (
        <Tooltip label={t('scan.close')}>
          <ActionIcon size={40} radius="xl" className={classes.close} onClick={onClose} aria-label={t('scan.close')}>
            <IconX size={22} stroke={2} />
          </ActionIcon>
        </Tooltip>
      )}
    </div>
  )
}
